/**
 *
 * TextInputBox
 *
 */
import * as React from 'react';
import styled from 'styled-components/macro';
import { ReactNode } from 'react';

interface ButtonConfig {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
  icon?: ReactNode;
}

interface Props {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  maxLength?: number;
  charCount?: boolean;
  buttons?: ButtonConfig[];
  onFocus?: () => void;
  onBlur?: () => void;
  focused?: boolean;
  tipMessage?: string;
}

const TipMessage = styled.div`
  position: absolute;
  bottom: -30px;
  left: 0;
  font-family: 'Raleway', sans-serif;
  font-size: 12px;
  color: #645270;
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;
`;

const Container = styled.div<{ $error?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  border: 1.5px solid ${({ $error }) => ($error ? '#FD6C6E' : '#645270')};
  border-radius: 15px;
  padding: 0 12px;
  width: 100%;
  background: transparent;

  &:focus-within {
    border-color: ${({ $error }) => ($error ? '#FD6C6E' : '#FF74E9')};
    transition: border-color 0.3s;
  }

  &:focus-within ${TipMessage} {
    opacity: 1;
  }
`;

const StyledTextarea = styled.textarea`
  flex: 1;
  background: transparent;
  color: #fff;
  border: none;
  outline: none;
  font-family: 'Raleway', sans-serif;
  font-size: 15px;
  font-weight: 400;
  resize: none;
  width: 100%;
  min-height: 80px;
  height: 80px;
  line-height: 1.4;
  padding: 10px 0 32px 0;
  &::placeholder {
    color: #645270;
    opacity: 1;
  }
  &:focus {
    outline: none;
    box-shadow: none;
    border: 0px;
  }
  &::-webkit-scrollbar {
    width: 8px;
    background: #180D23;
    border-radius: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: #332144;
    border-radius: 8px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #4a2e6e;
  }
  scrollbar-width: thin;
  scrollbar-color: #332144 #180D23;
`;

const BottomBar = styled.div`
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
  @media (max-width: 768px) {
    align-items: flex-end;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  pointer-events: auto;
  margin-left: auto;
  margin-right: 12px;
  margin-bottom: 3px;
  flex-wrap: wrap;
  justify-content: flex-start;
  @media (max-width: 768px) {
    width: 100%;
    margin-right: 0;
    margin-left: 0;
  }
`;

const StyledButton = styled.button<{variant?: string}>`
  background: ${({variant}) => variant === 'primary' ? '#FF74E9' : '#332144'};
  color: ${({variant}) => variant === 'primary' ? '#180D23' : '#F5E1FF'};
  border: none;
  border-radius: 6px;
  padding: 6px 18px;
  font-family: 'Raleway', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background 0.2s;
  &:hover {
    background: ${({variant}) => variant === 'primary' ? '#ff9cf2' : '#ff74e91f'};
  }
`;

const CharCount = styled.span<{ $error?: boolean }>`
  font-family: 'Raleway', sans-serif;
  font-size: 10px;
  color: ${({ $error }) => ($error ? '#FF4D4F' : '#645270')};
  padding: 0 2px;
  pointer-events: auto;
`;

export function TextInputBox({
  value,
  onChange,
  placeholder,
  maxLength = 300,
  charCount = true,
  buttons = [],
  onFocus,
  onBlur,
  tipMessage,
}: Props) {
  const isOverLimit = value.length > maxLength;
  return (
    <Container style={{height: '100%'}} $error={isOverLimit}>
      {tipMessage && <TipMessage>{tipMessage}</TipMessage>}
      <StyledTextarea
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        rows={3}
      />
      <BottomBar>
        <ButtonRow>
          {buttons.map((btn, i) => (
            <StyledButton key={btn.label + i} variant={btn.variant} type="button" onClick={btn.onClick}>
              {btn.icon && <span style={{marginRight: 8, display: 'flex', alignItems: 'center'}}>{btn.icon}</span>}
              {btn.label}
            </StyledButton>
          ))}
        </ButtonRow>
        {charCount && <CharCount $error={isOverLimit}>{value.length}/{maxLength}</CharCount>}
      </BottomBar>
    </Container>
  );
}
